import React, {createContext, ReactNode, useCallback, useContext, useState} from "react";

interface PageHeaderTitleProps {
	value: string | ReactNode;
	translationKey: boolean;
	icon?: string;
	/** Wrap the icon in the same rounded tile used on knowledge-base cards. */
	iconFramed?: boolean;
}

export type { PageHeaderTitleProps };

export interface PageHeaderProps {
	title: PageHeaderTitleProps;
	subtitle?: PageHeaderTitleProps;
	actions?: ReactNode;
	resetPageHeader?: () => void;
}

interface PageHeaderContextProps {
	pageHeader: PageHeaderProps;
	setPageHeader: (pageHeader: PageHeaderProps) => void;
	patchPageHeader: (pageHeader: Partial<PageHeaderProps>) => void;
	resetPageHeader: () => void;
}

const PageHeaderContext = createContext<PageHeaderContextProps | undefined>(undefined);

const EMPTY_PAGE_HEADER: PageHeaderProps = {
    title: { value: "", translationKey: false },
};

export const PageHeaderProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
	const [pageHeader, setPageHeader] = useState<PageHeaderProps>(EMPTY_PAGE_HEADER);

	const resetPageHeader = useCallback(() => {
		setPageHeader(EMPTY_PAGE_HEADER);
	}, []);

	const patchPageHeader = useCallback((patch: Partial<PageHeaderProps>) => {
		setPageHeader((previousHeader) => ({
			...previousHeader,
			...patch,
		}));
	}, []);

	return (
		<PageHeaderContext.Provider value={{pageHeader, setPageHeader, patchPageHeader, resetPageHeader}}>
			{children}
		</PageHeaderContext.Provider>
	);
}

export const usePageHeader = (): PageHeaderContextProps => {
	const context = useContext(PageHeaderContext);
	if (!context) {
		throw new Error("usePageHeader must be used within a PageHeaderProvider");
	}
	return context;
};